// ---------------------------------------------------------------------------
// SectionBlock Renderer (PB-E)
// ---------------------------------------------------------------------------
// Zone-bearing primitive — full-width semantic <section> with optional
// title/subtitle preamble and a children zone for nested blocks. Distinct
// from Container: SectionBlock spans full width, owns a header, and is the
// canonical landmark for "Hero + Pricing + FAQ"-style page outlines.

import { registerRenderer } from "../registry.js";
import type { PuckComponent, RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

type SectionAlign = "left" | "center" | "right";
type SectionTone = "default" | "muted" | "inverse";

interface SectionBlockProps {
  title?: string;
  subtitle?: string;
  headingLevel?: "h2" | "h3" | string;
  align?: SectionAlign | string;
  tone?: SectionTone | string;
  anchorId?: string;
  children?: unknown;
}

const VALID_ALIGN: ReadonlySet<SectionAlign> = new Set([
  "left",
  "center",
  "right",
]);
const VALID_TONE: ReadonlySet<SectionTone> = new Set([
  "default",
  "muted",
  "inverse",
]);
const VALID_HEADING: ReadonlySet<"h2" | "h3"> = new Set(["h2", "h3"]);
const SAFE_ANCHOR = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;

function resolveAlign(value: unknown): SectionAlign {
  return typeof value === "string" && VALID_ALIGN.has(value as SectionAlign)
    ? (value as SectionAlign)
    : "left";
}

function resolveTone(value: unknown): SectionTone {
  return typeof value === "string" && VALID_TONE.has(value as SectionTone)
    ? (value as SectionTone)
    : "default";
}

function resolveHeading(value: unknown): "h2" | "h3" {
  return typeof value === "string" && VALID_HEADING.has(value as "h2" | "h3")
    ? (value as "h2" | "h3")
    : "h2";
}

function resolveAnchor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return SAFE_ANCHOR.test(trimmed) ? trimmed : null;
}

function coerceChildren(value: unknown): PuckComponent[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is PuckComponent =>
      typeof entry === "object"
      && entry !== null
      && typeof (entry as PuckComponent).type === "string",
  );
}

function renderChildren(
  children: PuckComponent[],
  ctx: RenderContext,
): string {
  if (!ctx.renderBlock || children.length === 0) return "";
  return children.map((child) => ctx.renderBlock!(child)).join("\n");
}

function renderHeader(
  title: string,
  subtitle: string,
  level: "h2" | "h3",
  align: SectionAlign,
): string {
  if (!title.trim() && !subtitle.trim()) return "";
  const parts: string[] = [];
  if (title.trim()) {
    parts.push(
      `    <${level} class="ease-section-block__title">${text(title.trim())}</${level}>`,
    );
  }
  if (subtitle.trim()) {
    parts.push(
      `    <p class="ease-section-block__subtitle">${text(subtitle.trim())}</p>`,
    );
  }
  return `  <header class="ease-section-block__header" style="text-align: ${attr(align)};">
${parts.join("\n")}
  </header>`;
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  const p = props as SectionBlockProps;
  const align = resolveAlign(p.align);
  const tone = resolveTone(p.tone);
  const headingLevel = resolveHeading(p.headingLevel);
  const anchor = resolveAnchor(p.anchorId);

  const children = coerceChildren(p.children);
  const inner = renderChildren(children, ctx);
  const header = renderHeader(
    typeof p.title === "string" ? p.title : "",
    typeof p.subtitle === "string" ? p.subtitle : "",
    headingLevel,
    align,
  );

  const idAttr = anchor ? ` id="${attr(anchor)}"` : "";
  const body = inner
    ? `  <div class="ease-section-block__body">
${inner}
  </div>`
    : "  <!-- section-block empty -->";

  return `<section${idAttr} class="ease-section ease-section-block ease-section-block--tone-${attr(tone)}">
${[header, body].filter((part) => part.length > 0).join("\n")}
</section>`;
}

registerRenderer("section-block", { render });
